import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ObservationsService } from 'app/services/Observations/observations.service';
import { ObservationParamsOptions, ObservationRefactor } from 'app/models/observations.model';
import { MetaData } from 'app/models/shared.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslocoService } from '@ngneat/transloco';

export interface RelatedLogsDialogData {
  sequenceNumber: string;
  logType: 'MIR' | 'WIR';
  parentRecordNo: string;
  projectId: string;
}

@Component({
  selector: 'app-related-logs-dialog',
  templateUrl: './related-logs-dialog.component.html',
  styleUrls: ['./related-logs-dialog.component.scss'],
})
export class RelatedLogsDialogComponent implements OnInit, OnDestroy {
  isLoading = false;
  isRtl = false;
  logs: ObservationRefactor[] = [];
  meta: MetaData | null = null;
  pageIndex = 0;
  pageSize = 20;
  savedFilters: ObservationParamsOptions;

  displayedColumns = [
    'unique_id',
    'parent_record',
    'unifier_observation_id',
    'unifier_sequence_number',
    'unifier_total_record_revisions',
    'status',
    'title',
    'created_at',
    'updated_at',
    'creator',
    'cluster_name',
    'project_number',
    'action',
  ];

  private _unsubscribeAll: Subject<void> = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RelatedLogsDialogData,
    public dialogRef: MatDialogRef<RelatedLogsDialogComponent>,
    private observationsService: ObservationsService,
    private _transloco: TranslocoService,
  ) {}

  ngOnInit(): void {
    this.fetchLogs();
    this.isRtl = this._transloco.getActiveLang() === 'ar';
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  fetchLogs(): void {
    this.isLoading = true;

    const filter: ObservationParamsOptions = {
      logs_parent: this.data.sequenceNumber,
      logs_parent_type: this.data.logType,
      per_page: this.pageSize,
      page: this.pageIndex + 1,
      sort_by: 'created_at',
      sort_dir: 'desc',
      project_id: this.data.projectId,
      over_due_only: '0',
    };

    this.observationsService
      .getObservationsWithFilter(filter)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(
        (response) => {
          this.logs = response?.data || [];
          this.meta = (response?.meta_data as MetaData) || null;
          this.isLoading = false;
        },
        (error) => {
          console.error('Error fetching related logs:', error);
          this.isLoading = false;
        },
      );
  }

  onPageChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.fetchLogs();
  }

  openLogDetails(log: ObservationRefactor): void {
    if (!log?.id || !log?.project_id) return;

    // this.dialogRef.close();
    //open in new tab
    window.open(`/project-d/${log.project_id}/observations/details/${log.id}`, '_blank');
  }

  close(): void {
    this.dialogRef.close();
  }

  getStatusClass(isDraft: boolean, status: string): string {
    if (isDraft) {
      return 'bg-yellow-500';
    }

    const statusClassMap: { [key: string]: string } = {
      pending: 'pending',
      in_progress: 'inProgress',
      completed: 'completed',
      closed: 'closed',
      deleted_at: 'deleted_at',
      opened: 'opened',
      reviewed: 'reviewed',
      rejected: 'rejected',
      approved: 'approved',
    };

    return statusClassMap[status] || 'default-status';
  }

  getClusterName(log: ObservationRefactor): string {
    if (!log?.projects) {
      return '-';
    }

    // Traverse up the hierarchy to find the top-level parent (cluster)
    let currentProject = log.projects;
    while (currentProject.parent) {
      currentProject = currentProject.parent;
    }

    // Return the cluster name (top-level parent)
    return currentProject.project_name || currentProject.project_name_en || '-';
  }
}
